import React, { ReactNode, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SupabaseIcon } from '@/components/SupabaseIcon';
import { Badge } from '@/components/ui/badge';
import WorkshopNavigation from './WorkshopNavigation';
import { useWorkshopProgress, WorkshopStep } from '@/contexts/WorkshopProgressContext';
import {
  CheckCircle,
  Clock,
  Trophy,
  AlertCircle,
  Sparkles,
  Target,
  Users,
  ArrowRight,
  ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface WorkshopStepWrapperProps {
  children: ReactNode;
  step: WorkshopStep;
  title?: string;
  description?: string;
  className?: string;
  showNavigation?: boolean;
  showStepInfo?: boolean;
  showProgress?: boolean;
  showSkillsPreview?: boolean;
  autoMarkComplete?: boolean;
  requiredSkills?: number[];
  minInputLength?: number;
}

const STEP_THEMES = {
  landing: {
    bgColor: 'bg-gradient-to-br from-purple-50 to-blue-50',
    accent: 'text-purple-600',
    icon: Target
  },
  challenge: {
    bgColor: 'bg-gradient-to-br from-orange-50 to-red-50',
    accent: 'text-orange-600',
    icon: AlertCircle
  },
  practice: {
    bgColor: 'bg-gradient-to-br from-purple-50 to-indigo-50',
    accent: 'text-purple-600',
    icon: Users
  },
  transformation: {
    bgColor: 'bg-gradient-to-br from-green-50 to-emerald-50',
    accent: 'text-green-600',
    icon: Sparkles
  },
  assessment: {
    bgColor: 'bg-gradient-to-br from-yellow-50 to-orange-50',
    accent: 'text-yellow-600',
    icon: Trophy
  },
  cta: {
    bgColor: 'bg-gradient-to-br from-green-50 to-teal-50',
    accent: 'text-green-600',
    icon: CheckCircle
  }
};

export const WorkshopStepWrapper: React.FC<WorkshopStepWrapperProps> = ({
  children,
  step,
  title,
  description,
  className,
  showNavigation = true,
  showStepInfo = true,
  showProgress = true,
  showSkillsPreview = false,
  autoMarkComplete = false,
  requiredSkills = [],
  minInputLength = 0
}) => {
  const {
    state,
    dispatch,
    navigateToStep,
    masterSkill,
    getStepProgress,
    getMasteredSkillsCount,
    getWorkshopDuration,
    canNavigateNext
  } = useWorkshopProgress();

  const theme = STEP_THEMES[step];
  const IconComponent = theme.icon;
  const { current, total } = getStepProgress();
  const masteredSkills = getMasteredSkillsCount();
  const duration = getWorkshopDuration();

  // Auto-complete step if conditions are met
  useEffect(() => {
    if (autoMarkComplete && state.currentStep === step) {
      const shouldComplete = checkCompletionConditions();
      if (shouldComplete) {
        dispatch({ type: 'COMPLETE_STEP', payload: step });

        // Auto-master required skills
        requiredSkills.forEach(skillIndex => {
          masterSkill(skillIndex);
        });
      }
    }
  }, [state, step, autoMarkComplete, requiredSkills]);

  // Check if step completion conditions are met
  const checkCompletionConditions = (): boolean => {
    switch (step) {
      case 'landing':
        return true;
      case 'challenge':
        return true;
      case 'practice':
        return state.userInput.length >= minInputLength;
      case 'transformation':
        return state.improvementMade;
      case 'assessment':
        return masteredSkills >= 3;
      case 'cta':
        return masteredSkills >= 4;
      default:
        return false;
    }
  };

  // Get step completion status
  const isStepCompleted = state.completedSteps.includes(step);
  const canProceed = canNavigateNext();

  return (
    <div className={cn('min-h-screen', theme.bgColor)}>
      {/* Navigation Header */}
      {showNavigation && (
        <WorkshopNavigation
          showHeader={true}
          showBreadcrumbs={true}
          showProgress={showProgress}
          showStats={true}
        />
      )}

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Step Information Card */}
        {showStepInfo && (
          <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur-sm">
            <CardContent className="p-8">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-6">
                  <div className={cn('w-16 h-16 rounded-2xl flex items-center justify-center',
                    step === 'landing' && 'bg-purple-100',
                    step === 'challenge' && 'bg-orange-100',
                    step === 'practice' && 'bg-purple-100',
                    step === 'transformation' && 'bg-green-100',
                    step === 'assessment' && 'bg-yellow-100',
                    step === 'cta' && 'bg-green-100'
                  )}>
                    <IconComponent className={cn('w-8 h-8', theme.accent)} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h1 className="text-3xl font-bold text-gray-900">
                        {title || `Step ${current}: ${step.charAt(0).toUpperCase() + step.slice(1)}`}
                      </h1>
                      <Badge variant={isStepCompleted ? 'default' : 'secondary'} className="text-xs">
                        {isStepCompleted ? 'Completed' : 'In Progress'}
                      </Badge>
                    </div>
                    <p className="text-lg text-gray-600 mb-4">
                      {description || `Interactive workshop step focused on ${step} skills`}
                    </p>

                    {/* Step Stats */}
                    <div className="flex items-center space-x-6 text-sm text-gray-500">
                      {duration && (
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4" />
                          <span>Duration: {duration}</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-2">
                        <Trophy className="w-4 h-4" />
                        <span>Skills Mastered: {masteredSkills}/5</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Target className="w-4 h-4" />
                        <span>Progress: {Math.round((current / total) * 100)}%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lyra Avatar */}
                <div className="hidden md:flex items-center space-x-4">
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">Lyra AI Mentor</div>
                    <div className="text-xs text-gray-500">Your Workshop Guide</div>
                  </div>
                  <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center">
                    <SupabaseIcon
                      iconPath="lyra-avatar.png"
                      alt="Lyra AI Mentor"
                      size="md"
                      className="w-8 h-8"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Skills Preview */}
        {showSkillsPreview && (
          <Card className="mb-8 border border-purple-200 bg-white">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Trophy className="w-5 h-5 text-purple-600 mr-2" />
                Skills Progress
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {state.skills.map((skill, index) => (
                  <div
                    key={index}
                    className={cn(
                      'p-3 rounded-lg border transition-all',
                      skill.mastered
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : 'bg-gray-50 border-gray-200 text-gray-600'
                    )}
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      {skill.mastered ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                      )}
                      <span className="text-sm font-medium">{skill.skill}</span>
                    </div>
                    <p className="text-xs opacity-80">{skill.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Completion Requirements Alert */}
        {!canProceed && state.currentStep !== 'cta' && (
          <Alert className="mb-8 border-amber-200 bg-amber-50">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              {step === 'practice' && state.userInput.length < minInputLength &&
                `Please provide at least ${minInputLength} characters of input to continue.`}
              {step === 'transformation' && !state.improvementMade &&
                'Complete the transformation activity to proceed.'}
              {step === 'assessment' && masteredSkills < 3 &&
                'Master at least 3 skills to continue to the final step.'}
              {!checkCompletionConditions() &&
                'Complete the current step requirements to proceed.'}
            </AlertDescription>
          </Alert>
        )}

        {/* Main Content */}
        <div className={cn('space-y-8', className)}>
          {children}
        </div>

        {/* Step Completion Actions */}
        {isStepCompleted && (
          <Card className="mt-8 border-green-200 bg-green-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <h3 className="font-semibold text-green-900">Step Completed!</h3>
                    <p className="text-sm text-green-700">You've successfully completed this workshop step.</p>
                  </div>
                </div>
                <Button
                  onClick={() => {
                    const nextStep = current < total ? current + 1 : null;
                    if (nextStep) {
                      const stepNames: WorkshopStep[] = ['landing', 'challenge', 'practice', 'transformation', 'assessment', 'cta'];
                      navigateToStep(stepNames[nextStep - 1]);
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700"
                  disabled={current >= total}
                >
                  <span>Continue to Next Step</span>
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default WorkshopStepWrapper;